import React, { useEffect, useRef, useState, useCallback } from 'react'
// @ts-expect-error - d3-geo has no type declarations available
import { geoOrthographic, geoPath, geoGraticule10, type GeoPath } from 'd3-geo'
import type { Customer } from '@crm-src/types/customer'
import { companyInitials, isSafeLogoSrc } from '@crm-src/components/customer-world-map/logo-marker'
import { logoImage } from '@crm-src/components/customer-world-map/logo-images'
import { stepRotation } from '@crm-src/components/customer-world-map/globe-rotation'
import { crmApiUrl } from '@crm-src/lib/api'

// Natural Earth 110m land outline. Served locally from /public/data
// to avoid CDN/CSP issues.
const WORLD_GEOJSON_URL = '/data/ne_110m_land.geojson'

// Precomputed geometry that never changes
const SPHERE = { type: 'Sphere' } as const
const GRATICULE = geoGraticule10()

// Marker radius in CSS pixels. Bigger than the 5.5px dot this replaced: a logo
// has to be recognisable, and at 5px it is a coloured smudge. The globe shows a
// whole hemisphere at once, so its markers stay smaller than the flat map's
// 28px ones; the hit-test radius is sized to match.
const MARKER_RADIUS = 9
const MARKER_HIT_RADIUS_SQ = (MARKER_RADIUS + 5) ** 2

// Direct dot-product horizon test. d3's projection([lon,lat]) ignores clipAngle
// — clipAngle only applies to path streams — so we need this for markers.
const DEG = Math.PI / 180
function isPointVisible(lon: number, lat: number, rotLon: number, rotLat: number): boolean {
  const λ = (lon - rotLon) * DEG
  const φ = lat * DEG
  const φ0 = rotLat * DEG
  // cos(great-circle distance from view center) >= 0 → front hemisphere
  return Math.sin(φ0) * Math.sin(φ) + Math.cos(φ0) * Math.cos(φ) * Math.cos(λ) >= 0
}

interface CustomerWithCoords extends Customer {
  latitude?: number
  longitude?: number
}

interface Props {
  customers: CustomerWithCoords[]
  onCustomerClick?: (customer: CustomerWithCoords) => void
  getMarkerColor?: (customer: CustomerWithCoords) => string
  height?: number | string
}

export function CustomerWorldGlobe({
  customers,
  onCustomerClick,
  getMarkerColor,
  height = 720,
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [geojson, setGeojson] = useState<any>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [hovered, setHovered] = useState<CustomerWithCoords | null>(null)
  const [cursor, setCursor] = useState<{ x: number; y: number } | null>(null)
  // Fades the "Drag to rotate" hint out once the user actually grabs the globe.
  const [hasInteracted, setHasInteracted] = useState(false)

  // Mutable animation state (refs to avoid re-render churn)
  const rotRef = useRef<[number, number]>([20, -12]) // [lon, lat] in degrees
  const velRef = useRef<[number, number]>([0, 0])    // deg per frame
  const draggingRef = useRef(false)
  const lastPointerRef = useRef<[number, number, number] | null>(null) // [x, y, time]
  const lastInteractionRef = useRef<number>(Date.now() - 5000) // start idle so auto-rotate kicks in
  const sizeRef = useRef({ w: 0, h: 0, cx: 0, cy: 0, r: 0 })
  const markersRef = useRef<Array<{ c: CustomerWithCoords; x: number; y: number }>>([])
  const hoveredIdRef = useRef<number | string | null>(null)

  // A frame counter the logo cache can bump. The globe is frozen whenever the
  // pointer is on a marker and idle before auto-rotate kicks in, so a logo that
  // finishes decoding after the last paint would otherwise never appear.
  const [logoTick, setLogoTick] = useState(0)

  /**
   * This customer's decoded logo, or null to draw their initials instead.
   *
   * `logo_url` is a RELATIVE crm-api path; `crmApiUrl` resolves it against the
   * dashboard origin, which is what makes it load inside the bundled desktop
   * shell (no proxy at `tauri://`). It is validated first for the same reason
   * the flat map validates it — this ends up in an image request.
   */
  const logoFor = useCallback(
    (customer: CustomerWithCoords): HTMLImageElement | null => {
      const path = (customer as { logo_url?: string | null }).logo_url
      if (!path) return null
      const src = crmApiUrl(path)
      if (!isSafeLogoSrc(src)) return null
      return logoImage(src, () => setLogoTick((n) => n + 1))
    },
    [],
  )

  // ---------- Data loading ----------
  useEffect(() => {
    let aborted = false
    fetch(WORLD_GEOJSON_URL)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        return r.json()
      })
      .then((data) => {
        if (!aborted) setGeojson(data)
      })
      .catch((err) => {
        if (!aborted) setLoadError(err.message || 'Failed to load')
      })
    return () => {
      aborted = true
    }
  }, [])

  // ---------- DPR-aware sizing ----------
  useEffect(() => {
    const canvas = canvasRef.current
    const container = containerRef.current
    if (!canvas || !container) return

    const resize = () => {
      const rect = container.getBoundingClientRect()
      const dpr = window.devicePixelRatio || 1
      canvas.width = Math.max(1, Math.floor(rect.width * dpr))
      canvas.height = Math.max(1, Math.floor(rect.height * dpr))
      canvas.style.width = `${rect.width}px`
      canvas.style.height = `${rect.height}px`
      sizeRef.current = {
        w: rect.width,
        h: rect.height,
        cx: rect.width / 2,
        cy: rect.height / 2,
        r: Math.min(rect.width, rect.height) / 2 - 10,
      }
    }
    resize()
    const ro = new ResizeObserver(resize)
    ro.observe(container)
    return () => ro.disconnect()
  }, [])

  // Keep hover ID in a ref so the render loop can use it without restarting
  useEffect(() => {
    hoveredIdRef.current = hovered?.id ?? null
  }, [hovered])

  // ---------- Render loop ----------
  useEffect(() => {
    if (!geojson) return
    let rafId = 0

    const render = () => {
      const canvas = canvasRef.current
      if (!canvas) {
        rafId = requestAnimationFrame(render)
        return
      }
      const ctx = canvas.getContext('2d')
      if (!ctx) return

      const { w, h, cx, cy, r } = sizeRef.current
      if (w === 0 || h === 0 || r <= 0) {
        rafId = requestAnimationFrame(render)
        return
      }

      // ----- Physics: inertia, auto-rotate, and the hover freeze -----
      // The rule itself lives in `globe-rotation.ts` — pure, and tested there.
      // Pointing at a customer holds the globe still until the pointer moves
      // away; see that file for why.
      if (!draggingRef.current) {
        const step = stepRotation({
          rotation: rotRef.current,
          velocity: velRef.current,
          hovering: hoveredIdRef.current != null,
          idleMs: Date.now() - lastInteractionRef.current,
        })
        rotRef.current = step.rotation
        velRef.current = step.velocity
        if (step.keepAwake) lastInteractionRef.current = Date.now()
      }

      // ----- Build projection for this frame -----
      const projection = geoOrthographic()
        .scale(r)
        .translate([cx, cy])
        .rotate([-rotRef.current[0], -rotRef.current[1]])
        .clipAngle(90)
        .precision(0.4)

      const pathGen: GeoPath = geoPath(projection, ctx as any)

      const dpr = window.devicePixelRatio || 1
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      ctx.clearRect(0, 0, w, h)

      // ----- Outer halo glow -----
      const halo = ctx.createRadialGradient(cx, cy, r * 0.98, cx, cy, r + 42)
      halo.addColorStop(0, 'rgba(90, 160, 240, 0.38)')
      halo.addColorStop(1, 'rgba(90, 160, 240, 0)')
      ctx.fillStyle = halo
      ctx.beginPath()
      ctx.arc(cx, cy, r + 42, 0, Math.PI * 2)
      ctx.fill()

      // ----- Ocean (sphere fill via d3) -----
      const ocean = ctx.createRadialGradient(
        cx - r * 0.4,
        cy - r * 0.4,
        r * 0.08,
        cx,
        cy,
        r,
      )
      ocean.addColorStop(0, '#2e6394')
      ocean.addColorStop(0.55, '#133553')
      ocean.addColorStop(1, '#05101f')
      ctx.beginPath()
      pathGen(SPHERE as any)
      ctx.fillStyle = ocean
      ctx.fill()

      // ----- Graticule (properly clipped by d3) -----
      ctx.beginPath()
      pathGen(GRATICULE as any)
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.09)'
      ctx.lineWidth = 0.6
      ctx.stroke()

      // ----- Land fill (proper spherical clipping) -----
      ctx.beginPath()
      pathGen(geojson)
      ctx.fillStyle = '#3e7a4f'
      ctx.fill()

      // Land stroke — same path re-drawn so the geometry is consistent
      ctx.beginPath()
      pathGen(geojson)
      ctx.strokeStyle = '#24492e'
      ctx.lineWidth = 0.7
      ctx.stroke()

      // ----- Globe rim -----
      ctx.beginPath()
      pathGen(SPHERE as any)
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.32)'
      ctx.lineWidth = 1.2
      ctx.stroke()

      // ----- Customer markers (explicit back-hemisphere culling) -----
      const [rotLon, rotLat] = rotRef.current
      const projected: Array<{ c: CustomerWithCoords; x: number; y: number }> = []
      for (const c of customers) {
        if (c.latitude == null || c.longitude == null) continue
        const lat = Number(c.latitude)
        const lon = Number(c.longitude)
        if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue
        if (!isPointVisible(lon, lat, rotLon, rotLat)) continue
        const coord = projection([lon, lat])
        if (!coord) continue
        projected.push({ c, x: coord[0], y: coord[1] })
      }
      markersRef.current = projected

      // Shadows — one pass, so no marker casts a shadow onto its neighbour.
      for (const m of projected) {
        ctx.beginPath()
        ctx.arc(m.x, m.y + 1.5, MARKER_RADIUS, 0, Math.PI * 2)
        ctx.fillStyle = 'rgba(0, 0, 0, 0.5)'
        ctx.fill()
      }

      // Markers: the company's own logo on a white disc inside a coloured
      // ring, matching the flat map. The ring still carries everything the
      // plain dot carried (customer vs prospect, or the product palette), so
      // nothing is lost by adding the logo.
      for (const m of projected) {
        const color = getMarkerColor?.(m.c) ?? '#E74C3C'

        // White disc — the backdrop that makes a dark wordmark and a light one
        // both legible over ocean blue and land green.
        ctx.beginPath()
        ctx.arc(m.x, m.y, MARKER_RADIUS, 0, Math.PI * 2)
        ctx.fillStyle = '#ffffff'
        ctx.fill()

        const image = logoFor(m.c)
        if (image) {
          // Clip to the disc and letterbox the logo inside it: `contain`, not
          // `cover`, or a wide wordmark loses its ends to the crop.
          const inner = MARKER_RADIUS - 1.5
          const scale = Math.min((inner * 2) / image.naturalWidth, (inner * 2) / image.naturalHeight)
          const dw = image.naturalWidth * scale
          const dh = image.naturalHeight * scale
          ctx.save()
          ctx.beginPath()
          ctx.arc(m.x, m.y, inner, 0, Math.PI * 2)
          ctx.clip()
          ctx.drawImage(image, m.x - dw / 2, m.y - dh / 2, dw, dh)
          ctx.restore()
        } else {
          // No logo — the company's initials, not a blank disc. Most customers
          // have none and most never will (a prison and a county airport do not
          // publish a brand mark), so this is the common case, and an empty
          // white circle reads as a marker that failed.
          ctx.save()
          ctx.font = `600 ${MARKER_RADIUS}px Inter, system-ui, sans-serif`
          ctx.fillStyle = color
          ctx.textAlign = 'center'
          ctx.textBaseline = 'middle'
          ctx.fillText(companyInitials(m.c.company_name), m.x, m.y + 0.5)
          ctx.restore()
        }

        // Coloured ring, drawn last so it sits over the clipped logo edge.
        ctx.beginPath()
        ctx.arc(m.x, m.y, MARKER_RADIUS - 0.9, 0, Math.PI * 2)
        ctx.strokeStyle = color
        ctx.lineWidth = 1.8
        ctx.stroke()
      }

      // Hover ring (drawn on top)
      const hoveredId = hoveredIdRef.current
      if (hoveredId != null) {
        const hit = projected.find((m) => m.c.id === hoveredId)
        if (hit) {
          ctx.beginPath()
          ctx.arc(hit.x, hit.y, MARKER_RADIUS + 4, 0, Math.PI * 2)
          ctx.strokeStyle = '#ffffff'
          ctx.lineWidth = 2
          ctx.stroke()
        }
      }

      rafId = requestAnimationFrame(render)
    }

    rafId = requestAnimationFrame(render)
    return () => cancelAnimationFrame(rafId)
  }, [geojson, customers, getMarkerColor, logoFor, logoTick])

  // ---------- Pointer handlers ----------
  const onPointerDown = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    draggingRef.current = true
    lastPointerRef.current = [e.clientX, e.clientY, e.timeStamp]
    velRef.current = [0, 0] // reset inertia on new grab
    lastInteractionRef.current = Date.now()
    setHasInteracted(true)
    try {
      e.currentTarget.setPointerCapture(e.pointerId)
    } catch {}
  }, [])

  const onPointerMove = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current
    if (!canvas) return
    const rect = canvas.getBoundingClientRect()
    const localX = e.clientX - rect.left
    const localY = e.clientY - rect.top
    setCursor({ x: localX, y: localY })

    if (draggingRef.current && lastPointerRef.current) {
      const [lx, ly, lt] = lastPointerRef.current
      const dx = e.clientX - lx
      const dy = e.clientY - ly
      const dt = Math.max(1, e.timeStamp - lt)

      // Negative deltas: dragging right should carry the content right. With
      // d3's .rotate([-rotLon, ...]) convention, that means decreasing rotLon.
      const lonDelta = -dx * 0.32
      const latDelta = dy * 0.32

      // Apply rotation
      rotRef.current[0] = (rotRef.current[0] + lonDelta) % 360
      rotRef.current[1] = Math.max(-85, Math.min(85, rotRef.current[1] + latDelta))

      // Convert per-event delta → per-frame velocity (~16.67 ms per frame)
      const frameScale = 16.67 / dt
      const instLon = lonDelta * frameScale
      const instLat = latDelta * frameScale

      // EMA blend — heavy recency weight so release velocity matches feel
      velRef.current[0] = velRef.current[0] * 0.45 + instLon * 0.55
      velRef.current[1] = velRef.current[1] * 0.45 + instLat * 0.55

      lastPointerRef.current = [e.clientX, e.clientY, e.timeStamp]
      lastInteractionRef.current = Date.now()
      setHovered(null)
      return
    }

    // Hit-test for hover (marker radius² + a bit of slack)
    let hit: CustomerWithCoords | null = null
    for (const m of markersRef.current) {
      const dx = m.x - localX
      const dy = m.y - localY
      if (dx * dx + dy * dy < MARKER_HIT_RADIUS_SQ) {
        hit = m.c
        break
      }
    }
    setHovered(hit)
  }, [])

  const onPointerUp = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    draggingRef.current = false
    lastPointerRef.current = null
    lastInteractionRef.current = Date.now()
    try {
      e.currentTarget.releasePointerCapture(e.pointerId)
    } catch {}
  }, [])

  const onPointerLeave = useCallback(() => {
    setCursor(null)
    setHovered(null)
  }, [])

  const onClickCanvas = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (!onCustomerClick) return
      // Suppress click when the pointer actually dragged
      if (Math.hypot(velRef.current[0], velRef.current[1]) > 0.5) return
      const canvas = canvasRef.current
      if (!canvas) return
      const rect = canvas.getBoundingClientRect()
      const localX = e.clientX - rect.left
      const localY = e.clientY - rect.top
      for (const m of markersRef.current) {
        const dx = m.x - localX
        const dy = m.y - localY
        if (dx * dx + dy * dy < MARKER_HIT_RADIUS_SQ) {
          onCustomerClick(m.c)
          return
        }
      }
    },
    [onCustomerClick],
  )

  return (
    <div
      ref={containerRef}
      // Which customer the pointer is on, and therefore why the globe is
      // holding still. Rendered so the freeze is observable from outside the
      // canvas — a canvas has no DOM to assert against, and "did it stop?" is
      // the behaviour that matters here.
      data-globe-hovered={hovered ? String(hovered.id) : ''}
      style={{
        position: 'relative',
        width: '100%',
        height: typeof height === 'number' ? `${height}px` : height,
        background: 'radial-gradient(circle at 50% 50%, #0a1628 0%, #020510 85%)',
        borderRadius: '0.5rem',
        overflow: 'hidden',
        userSelect: 'none',
      }}
    >
      <canvas
        ref={canvasRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onPointerLeave={onPointerLeave}
        onClick={onClickCanvas}
        style={{
          display: 'block',
          width: '100%',
          height: '100%',
          cursor: draggingRef.current ? 'grabbing' : hovered ? 'pointer' : 'grab',
          touchAction: 'none',
        }}
      />

      {/* Tooltip */}
      {hovered && cursor && (
        <div
          style={{
            position: 'absolute',
            left: Math.min(cursor.x + 14, (sizeRef.current.w || 0) - 280),
            top: Math.min(cursor.y + 14, (sizeRef.current.h || 0) - 90),
            background: 'rgba(15, 23, 42, 0.92)', // slate-900 @ 92
            color: 'rgba(248, 250, 252, 0.96)',
            padding: '9px 13px',
            borderRadius: 8,
            fontSize: 12,
            pointerEvents: 'none',
            maxWidth: 280,
            boxShadow: '0 10px 28px rgba(2, 6, 23, 0.55)',
            border: '1px solid rgba(148, 163, 184, 0.22)', // slate-400 @ 22
            lineHeight: 1.45,
            backdropFilter: 'blur(4px)',
            WebkitBackdropFilter: 'blur(4px)',
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 2, letterSpacing: 0.1 }}>
            {hovered.company_name || 'No Company'}
          </div>
          <div style={{ opacity: 0.78 }}>
            {(hovered as any).city && `${(hovered as any).city}, `}
            {(hovered as any).country}
          </div>
          {(hovered as any).full_name && (
            <div style={{ opacity: 0.58, marginTop: 3 }}>
              {(hovered as any).full_name}
            </div>
          )}
        </div>
      )}

      {/* Loading / error states */}
      {!geojson && !loadError && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'rgba(203, 213, 225, 0.7)', // slate-300 @ 70
            fontSize: 13,
            letterSpacing: 0.2,
            pointerEvents: 'none',
          }}
        >
          Loading globe…
        </div>
      )}
      {loadError && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'rgba(252, 165, 165, 0.9)', // red-300 @ 90
            fontSize: 13,
            pointerEvents: 'none',
          }}
        >
          Failed to load world outline: {loadError}
        </div>
      )}

      {/* Hint — fades out after first interaction */}
      <div
        style={{
          position: 'absolute',
          bottom: 14,
          left: 16,
          fontSize: 11,
          color: 'rgba(226, 232, 240, 0.6)', // slate-200 @ 60
          pointerEvents: 'none',
          letterSpacing: 0.3,
          fontFeatureSettings: '"tnum"',
          opacity: hasInteracted ? 0 : 1,
          transform: hasInteracted ? 'translateY(4px)' : 'translateY(0)',
          transition: 'opacity 600ms cubic-bezier(0.22, 1, 0.36, 1), transform 600ms cubic-bezier(0.22, 1, 0.36, 1)',
        }}
      >
        Drag to rotate · fling for momentum · auto-rotates when idle
      </div>
    </div>
  )
}
